<template>
  <div>
    <div class="form-check">
      <input
        :id="limitedId"
        v-model="limited"
        class="form-check-input"
        type="checkbox"
        :disabled="disabled"
        data-testid="production-limited"
      />
      <label class="form-check-label" :for="limitedId">{{
        $t("forwards.mandates.production_range.limited")
      }}</label>
    </div>
    <div v-if="limited" class="mt-2" data-testid="production-range-months">
      <div class="row g-2">
        <div class="col-sm-6">
          <label class="form-label" :for="fromId">{{
            $t("forwards.mandates.production_range.first_month")
          }}</label>
          <MonthPickerComponent
            :modelValue="monthOfDate(from)"
            :inputId="fromId"
            :placeholder="$t('forwards.mandates.production_range.no_limit')"
            clearable
            :disabled
            data-testid="production-from"
            @update:modelValue="from = firstDayOfMonth($event)"
          />
        </div>
        <div class="col-sm-6">
          <label class="form-label" :for="untilId">{{
            $t("forwards.mandates.production_range.last_month")
          }}</label>
          <MonthPickerComponent
            :modelValue="monthOfDate(until)"
            :inputId="untilId"
            :placeholder="$t('forwards.mandates.production_range.no_limit')"
            clearable
            :disabled
            data-testid="production-until"
            @update:modelValue="until = lastDayOfMonth($event)"
          />
          <!-- d-block: the picker wraps its input, so `.is-invalid ~ .invalid-feedback` cannot reach this. -->
          <div
            v-if="error"
            class="invalid-feedback d-block"
            data-testid="production-range-error"
          >
            {{ $t("forwards.mandates.production_range.last_before_first") }}
          </div>
        </div>
      </div>
      <div class="form-text">
        {{ $t("forwards.mandates.production_range.hint") }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import MonthPickerComponent from "@/components/common/MonthPickerComponent.vue";
import type { ProductionRangeError } from "@/forwards2/mandates/mandateFormRules";
import {
  firstDayOfMonth,
  lastDayOfMonth,
  monthOfDate,
} from "@/forwards2/productionRange";
import { useId } from "vue";

/**
 * The production a recurring or pay-as-produced mandate sells to new deals, in whole months. The
 * parent shows it below the volumes in those two modes only; a one-off listing's rows already name
 * their periods.
 *
 * The models hold the wire's dates (`ProductionRange`), so the start is a month's first day and the
 * end its last. Unticking the box leaves the months in place until the save, which sends both ends
 * open (`mandateEditorForm.productionRangeOf`). A last month before the first is taken as typed and
 * explained beside it, the same as the other fields the write routes refuse (`mandateFormRules`).
 */
withDefaults(
  defineProps<{
    disabled?: boolean;
    error?: ProductionRangeError | null;
  }>(),
  { disabled: false, error: null }
);

const limited = defineModel<boolean>("limited", { required: true });
const from = defineModel<string | null>("from", { required: true });
const until = defineModel<string | null>("until", { required: true });

const limitedId = useId();
const fromId = useId();
const untilId = useId();
</script>
